/**
 * @file FormSheet.tsx
 * @brief Shared chrome for create/edit forms in a right Sheet + flush GridBlock.
 *
 * @details
 * Same visual language as SessionDetailDialog: rail Sheet, tinted section
 * bands, 2-col field cells, one-row Cancel | Submit footer. Prefer FormSheet
 * for admin create/edit/detail overlays; searchable pickers (language,
 * country, date, …) also use the right Sheet via `PickerSheet` /
 * date pickers (30-07-2026). Keep centered Dialog for confirms / Cmd+K.
 *
 * @updated 30-07-2026 — pickers share right-rail Sheet
 */

import type { ComponentPropsWithoutRef, HTMLAttributes, ReactNode } from "react";
import {
  GridBand,
  GridBlock,
  GridCell,
  GridCells,
} from "@/features/analytics/components/GridBlock";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/Sheet";
import { cn } from "@/shared/theme/cn";

export { FormFieldGroup } from "@/components/ui/FormDialog";

export const FormSheet = Sheet;
export const FormSheetTitle = SheetTitle;
export const FormSheetDescription = SheetDescription;

/** Full-height flush footer / cancel / submit button. */
/**
 * A footer button, the phone's `VZModalFooter` pair (DES 30-09-2026: "change
 * the button style like we have in the app"): a 56px pill, grey
 * (`quaternary`) by default, which is Cancel; the action adds the brand fill
 * (`bg-primary text-primary-foreground hover:bg-primary/90`).
 */
export const formSheetActionClass =
  "h-14 w-full justify-center gap-2 rounded-full bg-app-quaternary px-6 text-[15px] font-semibold text-app-text hover:bg-app-quaternary-dark";

export function FormSheetContent({
  className,
  children,
  side = "right",
  ...props
}: ComponentPropsWithoutRef<typeof SheetContent>) {
  return (
    <SheetContent
      side={side}
      className={cn(
        "flex flex-col gap-0 overflow-hidden p-0 shadow-xl",
        side === "bottom"
          ? "max-h-[85dvh]"
          : side === "palette"
            ? cn(
                "max-h-[calc(100dvh-4.5rem)]",
                // A page-wide frame makes the half-width 56px footer pills
                // huge (DES 01-10-2026: "buttons are too big"): in the
                // palette they sit on the right at their own width, 44px
                // tall, like the Cmd+K frame's actions.
                "[&_[data-form-footer]]:justify-end",
                "[&_[data-form-footer-cell]]:flex-none",
                "[&_[data-form-footer-cell]_button]:!h-11 [&_[data-form-footer-cell]_button]:!w-auto [&_[data-form-footer-cell]_button]:min-w-[148px] [&_[data-form-footer-cell]_button]:!px-6 [&_[data-form-footer-cell]_button]:!text-sm",
              )
            : "max-h-dvh border-l sm:max-w-xl lg:max-w-2xl",
        className,
      )}
      {...props}
    >
      {children}
    </SheetContent>
  );
}

/**
 * No divider lines inside a form (DES 30-09-2026, after trying it on Edit
 * user): the grid hairlines are hidden; field hairlines and the grey section
 * bands stay. Use it on any page form built from FormSheetFields outside a
 * FormSheetFrame.
 */
export const formGridLinesHiddenClass = String.raw`[&_.border-border\/60]:!border-transparent [&_.border-border\/80]:!border-transparent`;

/** Inner GridBlock frame — put this inside the `<form>`. */
export function FormSheetFrame({
  className,
  children,
}: HTMLAttributes<HTMLElement>) {
  return (
    <GridBlock
      className={cn(
        "flex min-h-0 flex-1 flex-col overflow-hidden border-l-0",
        formGridLinesHiddenClass,
        className,
      )}
    >
      {children}
    </GridBlock>
  );
}

export function FormSheetHeader({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <GridBand
      className={cn(
        "shrink-0 space-y-1.5 bg-background pr-12",
        className,
      )}
      {...props}
    >
      {children}
    </GridBand>
  );
}

export function FormSheetBody({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      // pb-10 = the footer's 40px fade (FormSheetFooter): the last thing in
      // the form can scroll clear of it instead of always sitting washed out
      // under it (DES 01-10-2026, "Activate Enterprise": "yeh blur kiyoon hai").
      className={cn("min-h-0 flex-1 overflow-y-auto bg-muted/10 pb-10", className)}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * Flush section — strong muted band + field cells on solid background so
 * Profile / Access / Security don’t read as one flat strip.
 */
export function FormSheetSection({
  title,
  subtitle,
  children,
  className,
  hideHeading = false,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  className?: string;
  /**
   * No title, when a tab already names the section (DES 01-10-2026: "when
   * we know we are in this section why do we need another heading"); the
   * subtitle still shows. `title` labels the region for screen readers.
   */
  hideHeading?: boolean;
}) {
  if (hideHeading) {
    // The tab names the section, so only the title goes; the subtitle stays
    // (DES 01-10-2026: "sub to rehnay daytay").
    return (
      <div role="group" aria-label={title} className={cn("bg-background", className)}>
        {/* A plain line, not a grey band: DES called the bands "the worst
            parts in the ui" (01-10-2026). Same side padding as the fields. */}
        {subtitle ? (
          <p className="px-4 pb-1 pt-4 text-[13px] leading-relaxed text-muted-foreground md:px-5">
            {subtitle}
          </p>
        ) : (
          <div className="pt-3" />
        )}
        {children}
      </div>
    );
  }
  return (
    <div className={cn("bg-background", className)}>
      {/* Plain heading, no grey band (DES 01-10-2026: the bands were "the
          worst parts in the ui"). Same side padding as the fields. */}
      <div className="px-4 pb-1 pt-4 md:px-5">
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-foreground/85">
          {title}
        </p>
        {subtitle ? (
          <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">{subtitle}</p>
        ) : null}
      </div>
      {children}
    </div>
  );
}

export function FormSheetFields({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <GridCells
      className={cn("grid-cols-1 sm:grid-cols-2", className)}
      {...props}
    >
      {children}
    </GridCells>
  );
}

export function FormSheetField({
  className,
  span = 1,
  padded = true,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement> & { span?: 1 | 2; padded?: boolean }) {
  return (
    <GridCell
      padded={padded}
      dense
      // 8px above and below, not the dense cell's 12 (DES 30-09-2026:
      // "reduce the field spaces a little").
      className={cn(padded && "py-2", span === 2 && "sm:col-span-2", className)}
      {...props}
    >
      {children}
    </GridCell>
  );
}

export function FormSheetFooter({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    // The phone's footer: no hairline above; a fade from the sheet colour
    // over the content is the separation. Buttons side by side, 8px apart,
    // 24px in from the sheet's edges.
    <div
      data-form-footer=""
      className={cn("relative flex shrink-0 gap-2 bg-background px-6 pb-6 pt-2", className)}
      {...props}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 -top-10 h-10 bg-gradient-to-b from-transparent to-background"
      />
      {children}
    </div>
  );
}

export function FormSheetFooterCell({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div data-form-footer-cell="" className={cn("flex min-w-0 flex-1", className)} {...props}>
      {children}
    </div>
  );
}

export function FormSheetAlert({ children }: { children: ReactNode }) {
  return (
    <GridCell>
      <div
        role="alert"
        className="border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive"
      >
        {children}
      </div>
    </GridCell>
  );
}
